import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname, parseError } from "../../../utils";

interface WordPressIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    domain?: string;
    username?: string;
    password?: string;
  };
  getToken?: Function;
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: {
    domain: string;
    username: string;
    password: string;
  }) => void;
}

export default function WordPressIntegrationForm({
  editMode = false,
  initialData,
  getToken,
  onValidationChange,
  onDataChange,
}: WordPressIntegrationFormProps) {
  const [domain, setDomain] = useState(initialData?.domain || "");
  const [username, setUsername] = useState(initialData?.username || "");
  const [password, setPassword] = useState(initialData?.password || "");

  // Test connection states
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    user?: { name: string; email: string; roles: string[] };
  } | null>(null);
  const [credentialsVerified, setCredentialsVerified] = useState(false);

  // Test WordPress credentials
  const testConnection = async () => {
    if (!domain.trim() || !username.trim() || !password.trim()) {
      setTestResult({
        success: false,
        message: "Please fill in all fields before testing the connection.",
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const token = await getToken?.();
      const response = await axios.post(
        `${hostname}/integrations/wordpress/test`,
        {
          domain: domain.trim(),
          username: username.trim(),
          password: password.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setTestResult({
          success: true,
          message: response.data.message,
          user: response.data.user,
        });
        setCredentialsVerified(true);
      }
    } catch (error: any) {
      const errorData = error.response?.data;
      setTestResult({
        success: false,
        message: errorData?.error || parseError(error),
      });
      setCredentialsVerified(false);
    } finally {
      setTesting(false);
    }
  };

  // Reset verification when credentials change
  useEffect(() => {
    setCredentialsVerified(false);
    setTestResult(null);
  }, [domain, username, password]);

  // Validate form - require verification for new installations
  useEffect(() => {
    const fieldsValid =
      domain.trim() !== "" && username.trim() !== "" && password.trim() !== "";
    const isValid = fieldsValid && (editMode || credentialsVerified);
    onValidationChange(isValid);
  }, [
    domain,
    username,
    password,
    credentialsVerified,
    editMode,
    onValidationChange,
  ]);

  // Notify parent of data changes
  useEffect(() => {
    onDataChange({
      domain,
      username,
      password,
    });
  }, [domain, username, password, onDataChange]);

  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ mb: 2 }}>
        Provide credentials for authenticating with the API
      </Typography>
      <TextField
        fullWidth
        sx={{ mb: 2 }}
        label="Wordpress Domain"
        placeholder="example.com"
        value={domain}
        onChange={(e) => setDomain(e.target.value)}
        required
      />
      <TextField
        fullWidth
        label="Username or Email"
        sx={{ mb: 2 }}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
      />
      <TextField
        fullWidth
        type="password"
        label="Password (Application Password)"
        helperText="Application passwords can be created in your wordpress admin site. https://{wordpress_domain}/wp-admin/profile.php"
        sx={{ mb: 2 }}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />

      {/* Test Connection Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          onClick={testConnection}
          disabled={
            testing || !domain.trim() || !username.trim() || !password.trim()
          }
          startIcon={testing ? <CircularProgress size={16} /> : null}
          sx={{ mb: 1 }}
        >
          {testing ? "Testing Connection..." : "Test Connection"}
        </Button>

        {credentialsVerified && (
          <Chip
            label="✓ Credentials Verified"
            color="success"
            size="small"
            sx={{ ml: 1 }}
          />
        )}
      </Box>

      {/* Test Results */}
      {testResult && (
        <Alert
          severity={testResult.success ? "success" : "error"}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="body2">{testResult.message}</Typography>
            {testResult.success && testResult.user && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  User: {testResult.user.name || testResult.user.email}
                  {testResult.user.roles.length > 0 && (
                    <> • Roles: {testResult.user.roles.join(", ")}</>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Alert>
      )}

      {!editMode && !credentialsVerified && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please test your credentials before proceeding with the installation.
        </Alert>
      )}
    </Box>
  );
}
